import {
    SmartCallModalHeader,
    SmartCallProfile,
    SmartCallTab,
    SmartCallTabs,
    useSmartCallOutletContext,
} from './CloudConnectorSmartCall.tsx';
import { Outlet, useNavigate, useOutletContext } from 'react-router';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../ui/LastUpdateTimestampAndRefresh.tsx';
import { HeaderBarWithRefreshButton } from '../parkingabo-users/OperatorParkingaboUsersDetail.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';

export function CloudConnectorSmartCallProfilesTab() {
    const {
        zoneId,
        onClose,
        navigateWithinModal,
        profilesState,
        refetchProfiles,
    } = useSmartCallOutletContext();
    const navigate = useNavigate();

    return (
        <OperatorRoutedModalContent
            header={
                <SmartCallModalHeader
                    buttons={<HeaderActionCloseButtons onClose={onClose} />}
                    editMode={false}
                />
            }
            bar={
                <SmartCallTabs
                    smartCallTab={SmartCallTab.PROFILES}
                    navigateWithinModal={navigateWithinModal}
                />
            }
            style={{ padding: '0px 0px' }}
            body={
                <OperatorAsyncLoadedSection
                    requestState={profilesState}
                    render={profiles => (
                        <>
                            <HeaderBarWithRefreshButton
                                button={
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => navigate('add')}
                                    >
                                        <Localized
                                            de="Hinzufügen"
                                            fr="Ajouter"
                                            it="Aggiungi"
                                            en="Add"
                                        />
                                    </Button>
                                }
                                refreshButton={
                                    <LastUpdateTimestampAndRefreshAsyncLoadedSection
                                        requestState={profilesState}
                                        onRefresh={refetchProfiles}
                                        render={(profiles, timestamp) => (
                                            <LastUpdateTimestampAndRefresh
                                                resultsCount={profiles.length}
                                                eof={true}
                                                onRefresh={refetchProfiles}
                                                timestamp={timestamp}
                                            />
                                        )}
                                    />
                                }
                            />
                            <SmartCallProfilesList
                                profiles={profiles}
                                refetchProfiles={refetchProfiles}
                            />

                            <SmartCallDetailsOutlet
                                context={{
                                    zoneId: zoneId,
                                    refetchProfiles: refetchProfiles,
                                }}
                            />
                        </>
                    )}
                />
            }
        />
    );
}

export function useSmartCallDetailsOutletContext() {
    return useOutletContext<SmartCallDetailsContext>();
}

function SmartCallDetailsOutlet({
    context,
}: {
    context: SmartCallDetailsContext;
}) {
    return <Outlet context={context} />;
}

export interface SmartCallDetailsContext {
    zoneId: string;
    refetchProfiles: () => void;
}

function SmartCallProfilesList({
    profiles,
    refetchProfiles,
}: {
    profiles: SmartCallProfile[];
    refetchProfiles: () => void;
}) {
    const navigate = useNavigate();
    const hasDeletableProfile = profiles.some(p => p.isDeletable);
    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Beschreibung"
                                fr="Description"
                                it="Descrizione"
                                en="Description"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Letzte Änderung"
                                fr="Dernière mise à jour"
                                it="Ultimo aggiornamento"
                                en="Last updated"
                            />
                        </TableCell>
                        {hasDeletableProfile && (
                            <TableCell>
                                <Localized
                                    de="Aktionen"
                                    fr="Actions"
                                    it="Azioni"
                                    en="Actions"
                                />
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {profiles.map(profile => (
                        <SmartCallProfileRow
                            key={profile.id}
                            profile={profile}
                            onClick={() => navigate(`${profile.id}/details`)}
                            refetchProfiles={refetchProfiles}
                            hasDeletableProfile={hasDeletableProfile}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function SmartCallProfileRow({
    profile,
    onClick,
    refetchProfiles,
    hasDeletableProfile,
}: {
    profile: SmartCallProfile;
    onClick: () => void;
    refetchProfiles: () => void;
    hasDeletableProfile: boolean;
}) {
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer', height: '54px' }}
            onClick={() => {
                if (deleteModalOpen) return;
                onClick();
            }}
        >
            <TableCell>{profile.description}</TableCell>
            <TableCell>
                {Formatter.dayMonthYearHourMinute(
                    DateTime.fromISO(
                        profile.lastChangedAt
                            ? profile.lastChangedAt
                            : profile.createdAt,
                    ),
                )}
            </TableCell>
            {hasDeletableProfile && (
                <TableCell>
                    {profile.isDeletable && (
                        <Tooltip
                            title={
                                <Localized
                                    de="Löschen"
                                    fr="Supprimer"
                                    it="Elimina"
                                    en="Delete"
                                />
                            }
                            placement="right"
                        >
                            <IconButton
                                color="primary"
                                onClick={(e: React.SyntheticEvent) => {
                                    e.stopPropagation();
                                    setDeleteModalOpen(true);
                                }}
                                sx={{ padding: 0 }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            )}
            <DeleteStackModal
                open={deleteModalOpen}
                closeModal={() => setDeleteModalOpen(false)}
                refetchProfiles={refetchProfiles}
                profile={profile}
            />
        </TableRow>
    );
}

function DeleteStackModal({
    open,
    closeModal,
    refetchProfiles,
    profile,
}: {
    open: boolean;
    closeModal: () => void;
    refetchProfiles: () => void;
    profile: SmartCallProfile;
}) {
    const [deleteSmartCallProfileState, deleteSmartCallProfile] =
        useServerWrite<null, null, null>(() => ({
            url: `/ui-api/operator-account/cloud-connector/smart-call/profile/${profile.id}/delete`,
            method: RequestMethod.PUT,
        }));
    useServerSuccessEffect(deleteSmartCallProfileState, () => {
        refetchProfiles();
        closeModal();
    });

    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Profil löschen"
                    fr="Supprimer le profil"
                    it="Eliminare profilo"
                    en="Delete profile"
                />
            }
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
            onConfirm={() => deleteSmartCallProfile()}
            confirmDisabled={
                deleteSmartCallProfileState.status === RequestStatus.PENDING
            }
            onCancel={() => closeModal()}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <BackendRequestErrorMessage
                    requestState={deleteSmartCallProfileState}
                />

                <p>
                    <Localized
                        de={`Wollen Sie wirklich das Profil «${profile.description}» löschen?`}
                        fr={`Voulez-vous vraiment supprimer le profil «${profile.description}»?`}
                        it={`Vuole veramente eliminare il profilo «${profile.description}»?`}
                        en={`Do you really want to delete the profile «${profile.description}»?`}
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}
